import { ArrowLeftIcon, CheckIcon, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { createDirectCheckoutSessionFunction } from "../data/firebase";

const includedFeatures = [
  "Generate unlimited replies",
  "Extract texts from screenshots",
  "Analyze every chat",
  "Realtime personalized coaching",
  "Battle-tested replies",
  "Built by real dating coaches enhanced by AI",
];

const frequencies = {
  monthly: {
    value: "monthly",
    label: "Monthly",
    priceSuffix: "/month",
    monthlyPrice: "20",
    totalPrice: "20",
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY,
  },
  yearly: {
    value: "yearly",
    label: "Yearly",
    priceSuffix: "/year",
    monthlyPrice: "15",
    totalPrice: "180",
    priceId: import.meta.env.VITE_STRIPE_PRICE_ID_YEARLY,
  },
};

export const Pricing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<"yearly" | "monthly">(
    "monthly"
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubscribe = async () => {
    if (!currentUser) {
      navigate("/register");
      return;
    } else {
      setIsLoading(true);
      try {
        const { data } = await createDirectCheckoutSessionFunction();
        window.location.href = (data as { url: string }).url;
      } catch (error) {
        console.error("Error creating checkout session:", error);
        // Already subscribed (stale claims) — the server re-synced them.
        const code = (error as { details?: { code?: string } })?.details?.code;
        if (code === "subscription_active" || code === "subscription_past_due") {
          navigate("/");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="bg-gray-900 w-full h-full overflow-y-auto">
      <div className="mx-auto max-w-7xl min-h-screen py-8 px-4 flex flex-col items-center overflow-y-auto bg-gray-900">
        <div className="flex flex-col items-center justify-center flex-1 w-full">
          <div className="w-full justify-start pb-4">
            <Link
              to="/"
              className="flex flex-row items-center gap-2 text-purple-400 hover:text-purple-300"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </Link>
          </div>

          <div className="bg-gray-800 rounded-3xl shadow-lg p-4 w-full border border-gray-700">
            <div className="mx-auto py-4 md:py-8 px-2 md:px-6 lg:px-8">
              <div className="mx-auto max-w-4xl md:text-center flex flex-col items-center gap-4">
                <div className="rounded-full bg-gradient-to-br from-purple-500 to-purple-800 px-4 py-1 text-sm font-semibold text-white w-fit">
                  <p className="text-center text-xl font-bold">Free Trial</p>
                </div>
                <h2 className="text-pretty text-2xl md:text-4xl font-semibold tracking-tight text-white">
                  Get Started With Options AI
                </h2>
                <p className="mx-auto max-w-2xl text-pretty text-lg font-medium text-gray-400 md:text-xl/8">
                  Unlock your personal AI wingman to 10x your dating life now.
                  More dates. More relationships. More options.
                </p>
              </div>
              {/* <div className="mt-4 flex justify-center">
      <AnimatedSwitch value={billingCycle} onChange={setBillingCycle} />
    </div> */}
              <div className="mt-8 mx-auto max-w-2xl rounded-3xl ring-1 ring-gray-700 lg:mx-0 flex lg:max-w-none flex-col-reverse md:flex-row">
                <div className="p-4 md:p-8 lg:flex-auto">
                  <h3 className="text-xl md:text-3xl font-semibold tracking-tight text-white">
                    Options AI Premium
                  </h3>
                  <p className="mt-6 text-base/7 text-gray-300">
                    Get unlimited access to the only AI wingman that wasn't
                    built by nerds. Options uses battle-tested dating formulas
                    supercharged by cutting edge GPT. Personalized for you.
                    Maximized for success.
                  </p>
                  <div className="mt-10 flex items-center gap-x-4">
                    <h4 className="flex-none text-sm/6 font-semibold text-purple-400">
                      What's included
                    </h4>
                    <div className="h-px flex-auto bg-gray-700" />
                  </div>
                  <ul
                    role="list"
                    className="mt-8 grid grid-cols-1 gap-4 text-sm/6 text-gray-300 md:grid-cols-2 md:gap-6"
                  >
                    {includedFeatures.map((feature) => (
                      <li key={feature} className="flex gap-x-3">
                        <CheckIcon
                          aria-hidden="true"
                          className="h-6 w-5 flex-none text-purple-400"
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="p-2 lg:mt-0 lg:w-full lg:max-w-md lg:shrink-0">
                  <div className="rounded-2xl bg-gray-900 py-12 md:py-16 ring-1 ring-inset ring-gray-700 lg:py-16 h-full">
                    <div className="h-full px-4 md:px-8 flex lex-col items-center justify-center w-full gap-3">
                      <div className="relative flex flex-col items-start justify-start gap-3 md:gap-4">
                        {/* {billingCycle === "yearly" && (
                    <div className="absolute -top-8 left-0 border border-green-300 rounded-full bg-green-50 px-2 py-1">
                      <p className="text-green-600 text-xs font-medium">
                        25% discount
                      </p>
                    </div>
                  )} */}
                        <p className="text-3xl md:text-4xl font-semibold tracking-tight text-white">
                          Try Free for 7 Days
                        </p>
                        <p className="text-base md:text-lg font-medium tracking-tight text-gray-300">
                          Then $19 / month
                        </p>
                        <Button
                          variant="custom"
                          className="rounded-md bg-purple-600 px-4 md:px-12 py-2 text-center text-base md:text-sm font-semibold text-white shadow-sm hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 hover:text-white w-full flex flex-row items-center justify-center gap-2"
                          onClick={handleSubscribe}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Please wait...
                            </>
                          ) : (
                            "Start your free trial"
                          )}
                        </Button>
                        <p className="text-xs text-gray-400 text-left w-64">
                          Your free trial will start immediately. <br />
                          After 7 days you will be billed $19 monthly.
                        </p>
                        {/* <div className="flex flex-col mt-1 text-left gap-1">
                <p className="text-xs font-semibold text-gray-600">
                  per month
                </p>
                <p className="text-xs font-semibold text-gray-600">
                  billed {frequencies[billingCycle].value}
                </p>
              </div> */}
                      </div>
                      {/* <p className="text-2xl font-semibold text-gray-900">
              7 day free trial
            </p>
            <p className="text-sm font-medium text-gray-500 mt-3 mb-3">
              then
            </p>
            <div className="flex items-start justify-center gap-x-2">
              <p className="text-5xl font-semibold tracking-tight text-gray-900">
                ${frequencies[billingCycle].monthlyPrice}
              </p>
              <div className="flex flex-col mt-1 text-left gap-1">
                <p className="text-xs font-semibold text-gray-600">
                  per month
                </p>
                <p className="text-xs font-semibold text-gray-600">
                  billed {frequencies[billingCycle].value}
                </p>
              </div>
            </div> */}
                      {/* <div className="w-full flex justify-center items-center">
              <Link
                to="#"
                className="mt-8 block rounded-md bg-purple-600 px-12 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 hover:text-white w-fit"
              >
                Start your free trial
              </Link>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Your free trial will start immediately. <br />
              After 7 days you will be billed $
              {frequencies[billingCycle].totalPrice}{" "}
              {frequencies[billingCycle].value}.
            </p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
